// Phase 3 (quote templates, plan 3.3 and 3.6): what the web works out from the
// templates the server returns. A template holds products and quantities only;
// its prices always come from today's catalog for its dealer.
import {
  checkProductQuotableV2,
  formatCents,
  normalizeTemplateName,
  type Cents,
  type CatalogProductV2,
  type ContractTerm,
  type Dealer,
  type PricedProduct,
  type QuoteLine,
  type QuoteTemplate,
  type QuoteTemplateDetail,
  type QuoteTemplateLine,
  type UnquotableTemplateLine,
} from "@phoneware/lens-domain";

/** A template's lines at today's list prices: how many there are, how many can be quoted now, and what they come to. */
export interface TemplateMoney {
  lines: number;
  ok: number;
  out: number;
  monthly: Cents;
  oneTime: Cents;
}

export function templateMoney(t: QuoteTemplateDetail): TemplateMoney {
  let monthly = 0;
  let oneTime = 0;
  for (const l of t.pricedLines) {
    if (l.billing === "monthly") monthly += l.listPrice * l.qty;
    else oneTime += l.listPrice * l.qty;
  }
  return { lines: t.lines.length, ok: t.pricedLines.length, out: t.unquotableLines.length, monthly, oneTime };
}

/**
 * The lines a quote started from a template opens with: each line the template can still quote, at the dealer's
 * PricedProduct.listPrice times the template's quantity, and the lines it can't quote now with the server's reason.
 * A line the server priced but the dealer's catalog no longer lists keeps the server's own price.
 */
export function startLines(t: QuoteTemplateDetail, catalog: PricedProduct[]): { lines: QuoteLine[]; left: UnquotableTemplateLine[] } {
  const refused = new Map(t.unquotableLines.map((l) => [l.productId, l]));
  const lines: QuoteLine[] = [];
  const left: UnquotableTemplateLine[] = [];
  for (const tl of t.lines) {
    const out = refused.get(tl.productId);
    if (out) {
      if (!left.some((l) => l.productId === tl.productId)) left.push(out);
      continue;
    }
    const have = lines.find((l) => l.productId === tl.productId);
    if (have) {
      have.qty += tl.qty;
      continue;
    }
    const p = catalog.find((x) => x.id === tl.productId);
    const priced = t.pricedLines.find((x) => x.productId === tl.productId);
    if (p && p.quotable) {
      lines.push({
        productId: p.id,
        code: p.code,
        name: p.name,
        billing: p.billing,
        unit: p.unit,
        qty: tl.qty,
        unitPrice: p.listPrice,
        listPrice: p.listPrice,
        floorPrice: p.floorPrice,
        buyPrice: p.buyPrice,
      });
    } else if (p) {
      left.push({ productId: p.id, qty: tl.qty, name: p.name, productName: p.name, reason: p.notQuotableReason ?? "Not available to quote yet.", listPrice: p.listPrice, period: p.billing });
    } else if (priced) {
      lines.push({ ...priced, qty: tl.qty, unitPrice: priced.listPrice });
    } else {
      left.push({ productId: tl.productId, qty: tl.qty, name: tl.productId, productName: tl.productId, reason: "Product not found in catalog.", listPrice: null, period: null });
    }
  }
  return { lines, left };
}

/**
 * A dealer's catalog as Phoneware ops sees it on that dealer's behalf: the server's dealer view of GET /api/catalog
 * (apps/lens/server/src/routes/catalog.ts), worked out from the whole record ops are sent.
 */
export function pricedForDealer(products: CatalogProductV2[], dealer: Dealer): PricedProduct[] {
  const priced: PricedProduct[] = [];
  for (const p of products) {
    // Dealers are never sent what is switched off, inactive, without a billing Omni can quote, or not linked to OneBill.
    if (!p.enabled || !p.active || p.billing === null || p.billingLink?.state !== "linked" || p.onebill === null) continue;
    const check = checkProductQuotableV2(p, { model: dealer.model });
    const noBuy = dealer.model === "wholesale" && p.wholesaleCost === null;
    priced.push({
      id: p.id,
      code: p.code,
      name: p.name,
      description: p.description,
      category: p.category,
      billing: p.billing,
      unit: p.unit,
      listPrice: p.listPrice,
      floorPrice: dealer.model === "agent" ? p.floorPrice : null,
      buyPrice: dealer.model === "wholesale" ? p.wholesaleCost : null,
      quotable: check.ok,
      notQuotableReason: noBuy ? "Product has no wholesale buy price." : check.ok ? null : (check.reason ?? null),
    });
  }
  return priced;
}

/** What a line the template can't quote now costs today, as the notice says it: "$276.00 a year in Autotask", "$209.00 each". */
export function leftOutPrice(line: Pick<UnquotableTemplateLine, "listPrice" | "period">): string | null {
  if (line.listPrice === null) return null;
  const money = formatCents(line.listPrice);
  switch (line.period) {
    case "monthly":
      return `${money} a month`;
    case "one_time":
      return `${money} each`;
    case "quarterly":
      return `${money} a quarter in Autotask`;
    case "semi_annual":
      return `${money} every six months in Autotask`;
    case "yearly":
      return `${money} a year in Autotask`;
    default:
      return money;
  }
}

/** Another of this dealer's templates already using the name, compared the way the server compares them. */
export function nameTaken(templates: QuoteTemplate[], dealerId: string, name: string, exceptId: string | null): QuoteTemplate | null {
  const wanted = normalizeTemplateName(name);
  if (!wanted) return null;
  return templates.find((t) => t.dealerId === dealerId && t.id !== exceptId && normalizeTemplateName(t.name) === wanted) ?? null;
}

/** The part of a template its sheet edits. */
export interface TemplateEdit {
  term: ContractTerm;
  pdfNotes: string;
  lines: QuoteTemplateLine[];
}

export function editOf(t: QuoteTemplate): TemplateEdit {
  return { term: t.term, pdfNotes: t.pdfNotes, lines: t.lines.map((l) => ({ productId: l.productId, qty: l.qty })) };
}

export function sameEdit(a: TemplateEdit, b: TemplateEdit): boolean {
  return a.term === b.term && a.pdfNotes === b.pdfNotes && JSON.stringify(a.lines) === JSON.stringify(b.lines);
}

const qtyIn = (e: TemplateEdit, productId: string): number | null => e.lines.find((l) => l.productId === productId)?.qty ?? null;

/** One thing that changed between the version opened and the two saved over it: a product's quantity (null when removed), the term or the PDF note. */
export interface ConflictRow {
  key: string;
  kind: "line" | "term" | "note";
  productId: string | null;
  before: number | ContractTerm | string | null;
  theirs: number | ContractTerm | string | null;
  mine: number | ContractTerm | string | null;
  /** Both changed it, to different values. */
  clash: boolean;
}

export function conflictRows(base: TemplateEdit, theirs: TemplateEdit, mine: TemplateEdit): ConflictRow[] {
  const ids: string[] = [];
  for (const v of [base, theirs, mine]) for (const l of v.lines) if (!ids.includes(l.productId)) ids.push(l.productId);
  const rows: ConflictRow[] = ids.map((id) => ({ key: `line:${id}`, kind: "line", productId: id, before: qtyIn(base, id), theirs: qtyIn(theirs, id), mine: qtyIn(mine, id), clash: false }));
  rows.push({ key: "term", kind: "term", productId: null, before: base.term, theirs: theirs.term, mine: mine.term, clash: false });
  rows.push({ key: "note", kind: "note", productId: null, before: base.pdfNotes, theirs: theirs.pdfNotes, mine: mine.pdfNotes, clash: false });
  return rows
    .filter((r) => r.theirs !== r.before || r.mine !== r.before)
    .map((r) => ({ ...r, clash: r.theirs !== r.before && r.mine !== r.before && r.mine !== r.theirs }));
}

/** Their version with only what I changed laid over it, for when we changed different things. */
export function applyMine(base: TemplateEdit, theirs: TemplateEdit, mine: TemplateEdit): TemplateEdit {
  const out: TemplateEdit = { term: theirs.term, pdfNotes: theirs.pdfNotes, lines: theirs.lines.map((l) => ({ ...l })) };
  const ids: string[] = [];
  for (const v of [base, mine]) for (const l of v.lines) if (!ids.includes(l.productId)) ids.push(l.productId);
  for (const id of ids) {
    const b = qtyIn(base, id);
    const m = qtyIn(mine, id);
    if (b === m) continue;
    const at = out.lines.findIndex((l) => l.productId === id);
    if (m === null) {
      if (at >= 0) out.lines.splice(at, 1);
    } else if (at >= 0) out.lines[at] = { productId: id, qty: m };
    else out.lines.push({ productId: id, qty: m });
  }
  if (mine.term !== base.term) out.term = mine.term;
  if (mine.pdfNotes !== base.pdfNotes) out.pdfNotes = mine.pdfNotes;
  return out;
}

/** A template's product names, from every version on screen, for lines that are no longer on the current one. */
export function productNames(...versions: QuoteTemplateDetail[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const v of versions) {
    for (const l of v.pricedLines) names.set(l.productId, l.name);
    for (const l of v.unquotableLines) names.set(l.productId, l.productName);
  }
  return names;
}
